/**
 * Technology Section - Business Value Calculator
 * Sprint 7: Day 1
 *
 * CapEx/OpEx split:
 * - CapEx: One-time capital expenditures (servers, storage, licenses)
 * - OpEx: Recurring operational expenses (cloud, SaaS) × 36 months
 * Total Technology = CapEx + (OpEx × 36)
 */

import React from 'react';

export interface TechnologyCost {
  capex: {
    servers: number;
    storage: number;
    licenses: number;
  };
  opex: {
    cloudHosting: number;
    saasSubscriptions: number;
  };
}

interface TechnologySectionProps {
  costs: TechnologyCost;
  onCostUpdate: (category: 'capex' | 'opex', field: string, value: number) => void;
}

export const TechnologySection: React.FC<TechnologySectionProps> = ({
  costs,
  onCostUpdate
}) => {
  // Calculate totals
  const capexTotal = costs.capex.servers + costs.capex.storage + costs.capex.licenses;
  const opexMonthly = costs.opex.cloudHosting + costs.opex.saasSubscriptions;
  const opexTotal = opexMonthly * 36; // 36-month period
  const technologyTotal = capexTotal + opexTotal;

  // Handle input change
  const handleInputChange = (
    category: 'capex' | 'opex',
    field: string,
    value: string
  ) => {
    const numericValue = parseFloat(value) || 0;

    // Validate range
    if (numericValue < 0 || numericValue > 10000000) return;

    onCostUpdate(category, field, numericValue);
  };

  return (
    <div className="technology-section">
      <div className="section-help-text">
        <strong>Technology Costs:</strong> Estimate one-time capital expenses (CapEx) and recurring operational expenses (OpEx).
        OpEx is calculated over 36 months. Include infrastructure, software licenses, and cloud services.
      </div>

      <div className="technology-split">
        {/* CapEx Column */}
        <div className="technology-category">
          <h4 className="technology-category-title">
            CapEx (One-Time)
            <span style={{ float: 'right', color: '#0094ab', fontSize: '1.125rem' }}>
              ${capexTotal.toLocaleString()}
            </span>
          </h4>

          <div className="parameter-field">
            <div className="parameter-label-group">
              <label className="parameter-label">Servers & Hardware</label>
              <p className="parameter-description">Physical or virtual server purchases</p>
            </div>
            <div className="parameter-input-group">
              <input
                type="number"
                className="parameter-input"
                min="0"
                step="1000"
                value={costs.capex.servers}
                onChange={(e) => handleInputChange('capex', 'servers', e.target.value)}
                aria-label="Servers and hardware cost"
              />
              <span className="parameter-unit">USD</span>
            </div>
          </div>

          <div className="parameter-field">
            <div className="parameter-label-group">
              <label className="parameter-label">Storage Infrastructure</label>
              <p className="parameter-description">Data lake, archive, backup storage</p>
            </div>
            <div className="parameter-input-group">
              <input
                type="number"
                className="parameter-input"
                min="0"
                step="1000"
                value={costs.capex.storage}
                onChange={(e) => handleInputChange('capex', 'storage', e.target.value)}
                aria-label="Storage infrastructure cost"
              />
              <span className="parameter-unit">USD</span>
            </div>
          </div>

          <div className="parameter-field">
            <div className="parameter-label-group">
              <label className="parameter-label">Software Licenses</label>
              <p className="parameter-description">Perpetual licenses, one-time purchases</p>
            </div>
            <div className="parameter-input-group">
              <input
                type="number"
                className="parameter-input"
                min="0"
                step="1000"
                value={costs.capex.licenses}
                onChange={(e) => handleInputChange('capex', 'licenses', e.target.value)}
                aria-label="Software licenses cost"
              />
              <span className="parameter-unit">USD</span>
            </div>
          </div>
        </div>

        {/* OpEx Column */}
        <div className="technology-category">
          <h4 className="technology-category-title">
            OpEx (Monthly × 36)
            <span style={{ float: 'right', color: '#0094ab', fontSize: '1.125rem' }}>
              ${opexTotal.toLocaleString()}
            </span>
          </h4>

          <div className="parameter-field">
            <div className="parameter-label-group">
              <label className="parameter-label">Cloud Hosting</label>
              <p className="parameter-description">AWS, Azure, GCP monthly fees</p>
            </div>
            <div className="parameter-input-group">
              <input
                type="number"
                className="parameter-input"
                min="0"
                step="100"
                value={costs.opex.cloudHosting}
                onChange={(e) => handleInputChange('opex', 'cloudHosting', e.target.value)}
                aria-label="Cloud hosting monthly cost"
              />
              <span className="parameter-unit">/mo</span>
            </div>
          </div>

          <div className="parameter-field">
            <div className="parameter-label-group">
              <label className="parameter-label">SaaS Subscriptions</label>
              <p className="parameter-description">Data tools, platforms, APIs</p>
            </div>
            <div className="parameter-input-group">
              <input
                type="number"
                className="parameter-input"
                min="0"
                step="100"
                value={costs.opex.saasSubscriptions}
                onChange={(e) => handleInputChange('opex', 'saasSubscriptions', e.target.value)}
                aria-label="SaaS subscriptions monthly cost"
              />
              <span className="parameter-unit">/mo</span>
            </div>
          </div>

          <div style={{ padding: '1rem', background: '#f7fafc', borderRadius: '8px', marginTop: '1rem' }}>
            <div style={{ fontSize: '0.75rem', color: '#718096', marginBottom: '0.5rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Monthly OpEx
            </div>
            <div style={{ fontSize: '1.125rem', fontWeight: 700, color: '#2d3748' }}>
              ${opexMonthly.toLocaleString()}/mo × 36 months
            </div>
          </div>
        </div>
      </div>

      <div style={{ marginTop: '1.5rem', padding: '1.25rem', background: 'linear-gradient(135deg, rgba(0, 148, 171, 0.1) 0%, rgba(0, 71, 114, 0.05) 100%)', borderRadius: '12px', border: '2px solid #0094ab' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontSize: '0.875rem', color: '#718096', marginBottom: '0.25rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Total Technology Costs
            </div>
            <div style={{ fontSize: '0.875rem', color: '#4a5568' }}>
              CapEx ${capexTotal.toLocaleString()} + OpEx ${opexTotal.toLocaleString()}
            </div>
          </div>
          <div style={{ fontSize: '2rem', fontWeight: 800, color: '#0094ab' }}>
            ${technologyTotal.toLocaleString()}
          </div>
        </div>
      </div>

      <style>{`
        .technology-split {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 2rem;
          margin-top: 1.5rem;
        }

        .technology-category {
          background: #f7fafc;
          border: 2px solid #e2e8f0;
          border-radius: 12px;
          padding: 1.5rem;
        }

        .technology-category-title {
          font-size: 1rem;
          font-weight: 700;
          color: #1a202c;
          margin: 0 0 1rem 0;
          padding-bottom: 0.75rem;
          border-bottom: 2px solid #e2e8f0;
        }

        .parameter-field {
          display: grid;
          grid-template-columns: 1fr auto;
          gap: 1.5rem;
          align-items: start;
          padding: 1.25rem 0;
          border-bottom: 1px solid #e2e8f0;
        }

        .parameter-field:last-of-type {
          border-bottom: none;
        }

        .parameter-label-group {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        .parameter-label {
          font-size: 1rem;
          font-weight: 600;
          color: #2d3748;
        }

        .parameter-description {
          font-size: 0.875rem;
          color: #718096;
          line-height: 1.5;
          margin: 0;
        }

        .parameter-input-group {
          display: flex;
          align-items: center;
          gap: 0.75rem;
        }

        .parameter-input {
          width: 140px;
          padding: 0.625rem 1rem;
          font-size: 1rem;
          font-weight: 600;
          text-align: right;
          border: 2px solid #e2e8f0;
          border-radius: 8px;
          background: #ffffff;
          color: #1a202c;
          transition: all 0.2s ease;
        }

        .parameter-input:focus {
          outline: none;
          border-color: #0094ab;
          box-shadow: 0 0 0 3px rgba(0, 148, 171, 0.1);
        }

        .parameter-unit {
          font-size: 0.875rem;
          font-weight: 600;
          color: #718096;
          min-width: 60px;
        }

        @media (max-width: 968px) {
          .technology-split {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </div>
  );
};

// Default technology costs
export const DEFAULT_TECHNOLOGY_COSTS: TechnologyCost = {
  capex: {
    servers: 50000,
    storage: 30000,
    licenses: 20000
  },
  opex: {
    cloudHosting: 3000,
    saasSubscriptions: 2000
  }
};

export default TechnologySection;
